import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useContest } from './ContestDialog';
import './contest-burst.css';

/** Comic «бах!» star: 12 uneven spikes in a 120×120 box. */
const burstPath = 'M60.0 3.0 L70.1 22.3 L85.0 16.7 L89.0 31.0 L107.6 32.5 L96.7 50.2 L108.0 60.0 L98.6 70.4 L109.4 88.5 L87.6 87.6 L85.5 104.2 L70.9 100.6 L60.0 114.0 L50.2 96.7 L35.5 102.4 L31.0 89.0 L11.5 88.0 L22.3 70.1 L10.0 60.0 L21.4 49.6 L14.1 33.5 L33.1 33.1 L36.0 18.4 L49.4 20.4Z';

/** Blocks the star must not cover, registered by `useContestBurstClear` (client only: effects never run in SSR). */
const clearBlocks = new Set<Element>();
const clearListeners = new Set<() => void>();
const notifyClearBlocks = () => { for (const listener of clearListeners) listener(); };

/** While the element passes under the «Конкурс!» star, the star steps aside (fades out and leaves the tab order). */
export function useContestBurstClear(ref: RefObject<Element | null>) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    clearBlocks.add(element);
    notifyClearBlocks();
    return () => {
      clearBlocks.delete(element);
      notifyClearBlocks();
    };
  }, [ref]);
}

/** Floating call to the contest on every public page while entries are open (the provider decides, SSR included). */
export function ContestBurst() {
  const openContest = useContest();
  const button = useRef<HTMLButtonElement>(null);
  const [yielding, setYielding] = useState(false);

  useEffect(() => {
    const star = button.current;
    if (!star) return;
    const covered = new Set<Element>();
    let observer: IntersectionObserver | null = null;
    // The observer's root area is the star's own resting box (offset* ignore the fade transform).
    const observe = () => {
      observer?.disconnect();
      covered.clear();
      setYielding(false);
      if (clearBlocks.size === 0) return;
      const top = star.offsetTop;
      const left = star.offsetLeft;
      const rootMargin = `${-top}px ${-(window.innerWidth - left - star.offsetWidth)}px ${-(window.innerHeight - top - star.offsetHeight)}px ${-left}px`;
      observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) covered.add(entry.target);
          else covered.delete(entry.target);
        }
        setYielding(covered.size > 0);
      }, { rootMargin });
      for (const block of clearBlocks) observer.observe(block);
    };
    observe();
    clearListeners.add(observe);
    window.addEventListener('resize', observe);
    return () => {
      clearListeners.delete(observe);
      window.removeEventListener('resize', observe);
      observer?.disconnect();
    };
  }, [openContest]);

  if (!openContest) return null;
  return <button className="contest-burst" ref={button} type="button" aria-label="Конкурс: участвовать" aria-haspopup="dialog" data-yielding={yielding || undefined} onClick={openContest}>
    <svg viewBox="0 0 120 120" aria-hidden="true" focusable="false">
      <path d={burstPath} fill="#ff7a1a" stroke="#1d1d1b" strokeWidth="4" strokeLinejoin="round" />
      <path d={burstPath} transform="translate(12 12) scale(.8)" fill="#ffd21f" stroke="#1d1d1b" strokeWidth="2.5" strokeLinejoin="round" />
      <text x="60" y="67" textAnchor="middle" textLength="64" lengthAdjust="spacingAndGlyphs" transform="rotate(-8 60 60)">Конкурс!</text>
    </svg>
  </button>;
}
